/*
 * Quiz runner — vanilla-JS port of the Hydrogen QuizPage/QuizRunner.
 *
 * The quiz is an API-driven, branching state machine served by the apps backend.
 * Endpoints (all require ONLY the `X-Shop-Domain` header — no bearer token):
 *   GET  {base}/quizzes?page=1&limit=100
 *   POST {base}/quizzes/:quizId/sessions
 *   POST {base}/quizzes/:quizId/sessions/:sessionId/answers
 *   POST {base}/quizzes/:quizId/sessions/:sessionId/complete
 *
 * Config is read from the mount element's data-* attributes (set in quiz.liquid):
 *   data-api-base      e.g. https://apps.bellavitaorganic.com/api/v1
 *   data-shop-domain   e.g. bella-vita-luxury.myshopify.com
 *   data-slug          the quiz slug (= page handle)
 *
 * NOTE: the backend must include this storefront's origin in ALLOWED_ORIGINS
 * (CORS) or the browser will block these requests.
 */
(function () {
  "use strict";

  // ----- Inline SVG icons (mirrors the React icon components) -----
  var ICONS = {
    check:
      '<svg class="quiz-option-indicator-check" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2 6.5L5 9.5L10 3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    arrow:
      '<svg class="quiz-btn-arrow" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3 7H11M11 7L7 3M11 7L7 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    star:
      '<svg class="quiz-product-rating-star" viewBox="0 0 12 12" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6 1l1.545 3.13L11 4.635 8.5 7.07l.59 3.44L6 8.89l-3.09 1.62L3.5 7.07 1 4.635l3.455-.505L6 1z"/></svg>',
    spinner:
      '<svg class="quiz-btn-spinner" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="2" opacity="0.25"/><path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    retake:
      '<svg class="quiz-retake-icon" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M11.5 7a4.5 4.5 0 1 1-1.32-3.18L11.5 5M11.5 2.5V5h-2.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  };

  // ----- Small helpers -----
  function escapeHtml(value) {
    if (value == null) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function escapeAttr(value) {
    return escapeHtml(value);
  }

  /** Convert a free-form title to a URL slug (matches the Hydrogen slugify). */
  function slugify(input) {
    if (typeof input !== "string") return "";
    return input
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  /** Storefront gids look like gid://shopify/ProductVariant/123 — the AJAX cart
   *  (`/cart/add.js`) needs the trailing numeric id only. */
  function numericVariantId(gid) {
    if (gid == null) return "";
    var str = String(gid);
    var parts = str.split("/");
    return parts[parts.length - 1] || str;
  }

  function formatMoney(amount, currencyCode) {
    var n = Number(amount);
    if (Number.isNaN(n)) return currencyCode + " " + amount;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCode,
        maximumFractionDigits: 2,
      }).format(n);
    } catch (e) {
      return currencyCode + " " + amount;
    }
  }

  // ----- API layer (mirrors app/lib/quiz/api.ts) -----
  function QuizApi(base, shopDomain) {
    this.base = String(base || "").replace(/\/$/, "");
    this.shopDomain = shopDomain;
  }

  QuizApi.prototype._call = function (path, options) {
    var opts = options || {};
    var url = this.base + path;
    var self = this;
    return fetch(url, {
      method: opts.method || "GET",
      headers: {
        "X-Shop-Domain": self.shopDomain,
        "Content-Type": "application/json",
      },
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    }).then(function (res) {
      return res
        .json()
        .catch(function () {
          return null;
        })
        .then(function (payload) {
          if (!res.ok || !payload || payload.success === false) {
            var msg =
              (payload && payload.message) ||
              "Quiz API request failed (" + res.status + ")";
            var err = new Error(msg);
            err.status = res.status;
            throw err;
          }
          return payload.data;
        });
    });
  };

  /** List quizzes and return the one whose slug/handle/title slugifies to `slug`. */
  QuizApi.prototype.findQuizBySlug = function (slug) {
    var target = String(slug || "").toLowerCase();
    return this._call("/quizzes?page=1&limit=100").then(function (data) {
      var list = Array.isArray(data)
        ? data
        : (data && (data.items || data.quizzes)) || [];
      var match = list.find(function (q) {
        var candidates = [q.slug, q.handle, q.title, q.name].filter(function (
          v
        ) {
          return typeof v === "string";
        });
        return candidates.some(function (c) {
          return slugify(c) === target;
        });
      });
      return match || null;
    });
  };

  QuizApi.prototype.startSession = function (quizId, sessionId) {
    return this._call("/quizzes/" + quizId + "/sessions", {
      method: "POST",
      body: sessionId ? { sessionId: sessionId } : {},
    });
  };

  QuizApi.prototype.submitAnswer = function (quizId, sessionId, body) {
    return this._call(
      "/quizzes/" + quizId + "/sessions/" + sessionId + "/answers",
      { method: "POST", body: body }
    );
  };

  QuizApi.prototype.completeSession = function (quizId, sessionId) {
    return this._call(
      "/quizzes/" + quizId + "/sessions/" + sessionId + "/complete",
      { method: "POST" }
    );
  };

  // ----- Runner -----
  function QuizRunner(root) {
    this.root = root;
    this.api = new QuizApi(
      root.getAttribute("data-api-base"),
      root.getAttribute("data-shop-domain")
    );
    this.slug = root.getAttribute("data-slug") || "";

    this.quizId = null;
    this.sessionId = null;
    this.totalQuestions = 0;
    // Stack of { question, selectedOptionIds: [] }; last entry = current question.
    this.stack = [];
    this.result = null; // { resultNode, products }
    this.error = null;
    this.submitting = false;

    this._onClick = this._onClick.bind(this);
    this.root.addEventListener("click", this._onClick);
  }

  QuizRunner.prototype.current = function () {
    return this.stack[this.stack.length - 1];
  };

  QuizRunner.prototype.start = function () {
    var self = this;
    self._watchBottomBars();
    self._renderStatus("Loading quiz…");
    self.api
      .findQuizBySlug(self.slug)
      .then(function (quiz) {
        if (!quiz) {
          var e = new Error("Quiz not found");
          e.status = 404;
          throw e;
        }
        self.quizId = quiz.id;
        return self.api.startSession(quiz.id);
      })
      .then(function (data) {
        self.sessionId = data.session.sessionId;
        self.totalQuestions = data.totalQuestions || 0;
        self.stack = [{ question: data.rootNode, selectedOptionIds: [] }];
        self.render();
      })
      .catch(function (err) {
        self._renderStatus(
          (err && err.message) || "Failed to load quiz",
          err && err.status === 503
        );
      });
  };

  // ----- Derived selection rules for the current question -----
  QuizRunner.prototype._rules = function () {
    var q = this.current().question;
    var maxSel = q.maxSelections == null ? null : q.maxSelections;
    var minSel = q.minSelections || 0;
    var isMulti = (q.maxSelections == null ? 1 : q.maxSelections) !== 1;
    var selected = this.current().selectedOptionIds;
    var canContinue =
      selected.length >= minSel && (maxSel == null || selected.length <= maxSel);
    return {
      maxSel: maxSel,
      minSel: minSel,
      isMulti: isMulti,
      selected: selected,
      canContinue: canContinue,
    };
  };

  // ----- Mutations -----
  QuizRunner.prototype.toggleOption = function (optionId) {
    this.error = null;
    var rules = this._rules();
    var top = this.current();
    var has = top.selectedOptionIds.indexOf(optionId) !== -1;
    if (rules.isMulti) {
      if (has) {
        top.selectedOptionIds = top.selectedOptionIds.filter(function (id) {
          return id !== optionId;
        });
      } else if (rules.maxSel != null && top.selectedOptionIds.length >= rules.maxSel) {
        return; // at cap; ignore
      } else {
        top.selectedOptionIds = top.selectedOptionIds.concat([optionId]);
      }
    } else {
      top.selectedOptionIds = [optionId];
    }
    this.render();
  };

  QuizRunner.prototype.handleBack = function () {
    this.error = null;
    if (this.stack.length === 1) {
      // First step — leave the quiz (browser back, falling back to home).
      if (window.history.length > 1) window.history.back();
      else window.location.href = "/";
      return;
    }
    this.stack.pop();
    this.render();
  };

  QuizRunner.prototype.handleRestart = function () {
    window.location.reload();
  };

  QuizRunner.prototype.handleContinue = function () {
    var self = this;
    var rules = this._rules();
    if (!rules.canContinue || self.submitting) return;
    var current = self.current();
    self.submitting = true;
    self.render();
    self.api
      .submitAnswer(self.quizId, self.sessionId, {
        questionNodeId: current.question.id,
        selectedOptionIds: rules.selected,
      })
      .then(function (data) {
        if (data.totalQuestions != null) self.totalQuestions = data.totalQuestions;
        if (data.nextNode && data.nextNode.type === "question") {
          self.stack.push({ question: data.nextNode, selectedOptionIds: [] });
          self.submitting = false;
          self.render();
        } else {
          // Reached a result node — fetch the completion payload.
          return self.api
            .completeSession(self.quizId, self.sessionId)
            .then(function (completion) {
              self.result = completion;
              self.submitting = false;
              self.render();
            });
        }
      })
      .catch(function (err) {
        self.error = (err && err.message) || "Quiz API request failed";
        self.submitting = false;
        self.render();
      });
  };

  // ----- Event delegation -----
  QuizRunner.prototype._onClick = function (e) {
    var el = e.target.closest("[data-quiz-action]");
    if (!el || !this.root.contains(el)) return;
    var action = el.getAttribute("data-quiz-action");
    if (action === "toggle") {
      if (el.getAttribute("aria-disabled") === "true" || el.disabled) return;
      this.toggleOption(el.getAttribute("data-option-id"));
    } else if (action === "continue") {
      this.handleContinue();
    } else if (action === "back") {
      this.handleBack();
    } else if (action === "restart") {
      this.handleRestart();
    } else if (action === "add") {
      this._addToCart(el);
    }
  };

  // ----- Add to cart via the theme's AJAX cart -----
  // Mirrors the theme's <product-form> flow (see global.js ProductForm):
  // request the cart drawer's sections in the add call, then hand the
  // response to <mini-cart>.renderContents() which re-renders and opens the
  // drawer — exactly like clicking ATC anywhere else on the store.
  QuizRunner.prototype._addToCart = function (button) {
    var variantId = button.getAttribute("data-variant-id");
    if (!variantId || button.disabled) return;
    var original = button.innerHTML;
    button.disabled = true;
    button.textContent = "Adding…";

    var miniCart = document.querySelector("mini-cart");
    var cartAddUrl =
      (window.routes && window.routes.cart_add_url) || "/cart/add";

    var body = { items: [{ id: variantId, quantity: 1 }] };
    if (miniCart && typeof miniCart.getSectionsToRender === "function") {
      body.sections = miniCart.getSectionsToRender().map(function (s) {
        return s.id;
      });
      body.sections_url = window.location.pathname;
    }

    fetch(cartAddUrl + ".js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/javascript",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(body),
    })
      .then(function (res) {
        if (!res.ok) throw new Error("add failed");
        return res.json();
      })
      .then(function (response) {
        // Shopify returns { status, description } on an add error.
        if (response && response.status) throw new Error(response.description);
        // Let the theme's cart drawer / bubble know something was added.
        document.dispatchEvent(new CustomEvent("quiz:added-to-cart"));
        // Re-render + open the theme's cart drawer when available; otherwise
        // fall back to the inline "Added ✓" confirmation.
        if (
          miniCart &&
          response &&
          response.sections &&
          typeof miniCart.renderContents === "function"
        ) {
          miniCart.renderContents(response);
        }
        button.textContent = "Added ✓";
        setTimeout(function () {
          button.innerHTML = original;
          button.disabled = false;
        }, 1500);
      })
      .catch(function () {
        button.textContent = "Try again";
        button.disabled = false;
        setTimeout(function () {
          button.innerHTML = original;
        }, 1500);
      });
  };

  // ----- Rendering -----
  QuizRunner.prototype._renderStatus = function (message, isConfigError) {
    this.root.className = "quiz-page";
    this.root.innerHTML =
      '<div class="quiz-status">' +
      escapeHtml(message) +
      (isConfigError
        ? "<br><small>The quiz API is not configured yet.</small>"
        : "") +
      "</div>";
  };

  QuizRunner.prototype.render = function () {
    if (this.result) {
      this._renderResult();
      this._unlockScroll(true);
      return;
    }
    this._renderQuestion();
    this._lockScroll();
  };

  QuizRunner.prototype._selectionHint = function (rules) {
    if (!rules.isMulti) return "";
    var min = rules.minSel;
    var max = rules.maxSel;
    var text;
    if (max != null && min === max)
      text = "Select " + min + " option" + (min === 1 ? "" : "s") + " :";
    else if (max != null)
      text = "Select Upto " + max + " Option" + (max === 1 ? "" : "s") + " :";
    else text = "Select at least " + min + " option" + (min === 1 ? "" : "s") + " :";
    return '<p class="quiz-question-hint">' + escapeHtml(text) + "</p>";
  };

  QuizRunner.prototype._renderQuestion = function () {
    var rules = this._rules();
    var q = this.current().question;
    var stepNum = this.stack.length;
    var self = this;

    var optionsHtml = q.options
      .map(function (option) {
        var isSelected = rules.selected.indexOf(option.id) !== -1;
        var atCap =
          rules.isMulti &&
          !isSelected &&
          rules.maxSel != null &&
          rules.selected.length >= rules.maxSel;
        var indicator = rules.isMulti
          ? '<span class="quiz-option-indicator-square">' + ICONS.check + "</span>"
          : '<span class="quiz-option-indicator-circle">' + ICONS.check + "</span>";
        var icon = option.iconUrl
          ? '<img src="' +
            escapeAttr(option.iconUrl) +
            '" alt="option_icon" class="quiz-option-icon' +
            (isSelected ? "" : " quiz-option-icon--dim") +
            '" aria-hidden="true">'
          : "";
        var desc = option.secondaryText
          ? '<span class="quiz-option-description">' +
            escapeHtml(option.secondaryText) +
            "</span>"
          : "";
        return (
          '<button type="button" data-quiz-action="toggle" data-option-id="' +
          escapeAttr(option.id) +
          '" role="' +
          (rules.isMulti ? "checkbox" : "radio") +
          '" aria-checked="' +
          (isSelected ? "true" : "false") +
          '"' +
          (atCap ? ' aria-disabled="true"' : "") +
          ' class="quiz-option' +
          (isSelected ? " quiz-option--selected" : "") +
          (atCap ? " quiz-option--disabled" : "") +
          '"' +
          (self.submitting ? " disabled" : "") +
          ">" +
          '<span class="quiz-option-content">' +
          icon +
          '<span class="quiz-option-text">' +
          '<span class="quiz-option-label">' +
          escapeHtml(option.text) +
          "</span>" +
          desc +
          "</span></span>" +
          '<span class="quiz-option-indicator">' +
          indicator +
          "</span>" +
          "</button>"
        );
      })
      .join("");

    var progressHtml = "";
    if (this.totalQuestions > 0) {
      var segs = "";
      for (var i = 0; i < this.totalQuestions; i++) {
        segs +=
          '<span class="quiz-progress-segment' +
          (i < stepNum ? " quiz-progress-segment--active" : "") +
          '"></span>';
      }
      progressHtml =
        '<div class="quiz-progress" role="progressbar" aria-valuemin="0" aria-valuemax="' +
        this.totalQuestions +
        '" aria-valuenow="' +
        Math.min(stepNum, this.totalQuestions) +
        '">' +
        segs +
        "</div>";
    }

    var continueInner = this.submitting
      ? ICONS.spinner + "Loading"
      : "Continue" + ICONS.arrow;

    this.root.className = "quiz-page";
    this.root.innerHTML =
      '<div class="quiz-container">' +
      '<div class="quiz-body">' +
      '<header class="quiz-header">' +
      '<p class="quiz-step-counter">Question ' +
      stepNum +
      "</p>" +
      '<h1 class="quiz-question-title">' +
      escapeHtml(q.text) +
      "</h1>" +
      (q.secondaryText
        ? '<p class="quiz-question-subtitle">' +
          escapeHtml(q.secondaryText) +
          "</p>"
        : "") +
      this._selectionHint(rules) +
      "</header>" +
      '<div class="quiz-options' +
      (q.options.length >= 5 ? " quiz-options--grid" : "") +
      '" role="' +
      (rules.isMulti ? "group" : "radiogroup") +
      '">' +
      optionsHtml +
      "</div>" +
      (this.error
        ? '<p class="quiz-error">' + escapeHtml(this.error) + "</p>"
        : "") +
      "</div>" +
      '<footer class="quiz-footer">' +
      progressHtml +
      '<div class="quiz-actions">' +
      '<button type="button" class="quiz-btn quiz-btn--secondary" data-quiz-action="back"' +
      (this.submitting ? " disabled" : "") +
      ">Go Back</button>" +
      '<button type="button" class="quiz-btn quiz-btn--primary" data-quiz-action="continue"' +
      (!rules.canContinue || this.submitting ? " disabled" : "") +
      ">" +
      continueInner +
      "</button>" +
      "</div>" +
      "</footer>" +
      "</div>";
  };

  QuizRunner.prototype._productCard = function (p) {
    var variants = p.variants || [];
    var variant =
      variants.filter(function (v) {
        return v.availableForSale;
      })[0] || variants[0];
    var sold = !(variant && variant.availableForSale);
    var currency =
      (p.priceRange &&
        p.priceRange.minVariantPrice &&
        p.priceRange.minVariantPrice.currencyCode) ||
      "INR";
    var priceAmount = Number(
      (variant && variant.price) ||
        (p.priceRange &&
          p.priceRange.minVariantPrice &&
          p.priceRange.minVariantPrice.amount) ||
        0
    );
    var compareAmount =
      variant && variant.compareAtPrice ? Number(variant.compareAtPrice) : null;
    var hasDiscount = compareAmount != null && compareAmount > priceAmount;
    var savings = hasDiscount ? compareAmount - priceAmount : 0;

    var tags = (Array.isArray(p.tags) ? p.tags : [])
      .filter(function (t) {
        return typeof t === "string";
      })
      .filter(function (t) {
        return !/^upsell[-_]/i.test(t);
      })
      .slice(0, 2);

    var badge = null;
    if (Array.isArray(p.label)) {
      badge =
        p.label.filter(function (l) {
          return typeof l === "string" && l.trim().length > 0;
        })[0] || null;
    } else if (typeof p.label === "string" && p.label.trim()) {
      badge = p.label.trim();
    }

    var ratingValue =
      typeof p.rating === "number"
        ? p.rating
        : p.rating && typeof p.rating === "object"
        ? Number(p.rating.value)
        : NaN;
    var rating = isFinite(ratingValue) ? ratingValue : null;

    var image =
      p.featuredImage && p.featuredImage.url
        ? '<img src="' +
          escapeAttr(p.featuredImage.url) +
          '" alt="' +
          escapeAttr(p.featuredImage.altText || p.title) +
          '" class="quiz-product-image" loading="lazy">'
        : "";

    var addBtn =
      sold || !variant
        ? '<button type="button" class="quiz-product-add" disabled>' +
          (sold ? "Sold Out" : "Unavailable") +
          "</button>"
        : '<button type="button" class="quiz-product-add" data-quiz-action="add" data-variant-id="' +
          escapeAttr(numericVariantId(variant.id)) +
          '">Add To Cart</button>';

    return (
      '<li class="quiz-product">' +
      '<a href="/products/' +
      escapeAttr(p.handle) +
      '" class="quiz-product-link">' +
      '<div class="quiz-product-image-wrap">' +
      (badge
        ? '<span class="quiz-product-badge">' +
          escapeHtml(badge.toUpperCase()) +
          "</span>"
        : "") +
      image +
      (rating != null
        ? '<span class="quiz-product-rating">' +
          ICONS.star +
          rating.toFixed(1) +
          "</span>"
        : "") +
      "</div>" +
      '<span class="quiz-product-title">' +
      escapeHtml(p.title) +
      "</span>" +
      '<span class="quiz-product-tags">' +
      (tags.length
        ? tags
            .map(function (t) {
              return escapeHtml(t.toUpperCase());
            })
            .join(" | ")
        : "&nbsp;") +
      "</span>" +
      (hasDiscount
        ? '<span class="quiz-product-savings">(Saving ' +
          escapeHtml(formatMoney(savings.toString(), currency)) +
          ")</span>"
        : "") +
      '<span class="quiz-product-price-row">' +
      '<span class="quiz-product-price">' +
      escapeHtml(formatMoney(priceAmount.toString(), currency)) +
      "</span>" +
      (hasDiscount && compareAmount != null
        ? '<span class="quiz-product-compare">' +
          escapeHtml(formatMoney(compareAmount.toString(), currency)) +
          "</span>"
        : "") +
      "</span>" +
      "</a>" +
      addBtn +
      "</li>"
    );
  };

  QuizRunner.prototype._renderResult = function () {
    var r = this.result.resultNode;
    var products = this.result.products || [];
    var hasCta = Boolean(r.ctaText && r.ctaUrl);
    var self = this;

    var productsHtml =
      products.length === 0
        ? '<p class="quiz-status">' +
          (hasCta
            ? "We couldn't find a perfect match."
            : "No matches yet — browse all products.") +
          "</p>"
        : '<ul class="quiz-products">' +
          products
            .map(function (p) {
              return self._productCard(p);
            })
            .join("") +
          "</ul>";

    this.root.className = "quiz-page quiz-page--flow";
    this.root.innerHTML =
      '<div class="quiz-container quiz-container--result">' +
      '<div class="quiz-body">' +
      '<header class="quiz-header quiz-header--result">' +
      '<div class="quiz-header-titles">' +
      '<h1 class="quiz-question-title">' +
      escapeHtml(r.text) +
      "</h1>" +
      (r.secondaryText
        ? '<p class="quiz-question-subtitle">' +
          escapeHtml(r.secondaryText) +
          "</p>"
        : "") +
      "</div>" +
      '<button type="button" class="quiz-retake quiz-retake--inline" data-quiz-action="restart">' +
      ICONS.retake +
      "Retake Quiz</button>" +
      "</header>" +
      productsHtml +
      "</div>" +
      '<footer class="quiz-footer quiz-footer--result">' +
      (hasCta
        ? '<a href="' +
          escapeAttr(r.ctaUrl) +
          '" class="animated-button animated-button--quiz">' +
          escapeHtml(r.ctaText) +
          ICONS.arrow +
          "</a>"
        : "") +
      '<button type="button" class="quiz-retake" data-quiz-action="restart">' +
      ICONS.retake +
      "Retake Quiz</button>" +
      "</footer>" +
      "</div>";
  };

  // ----- Hide site chrome that doesn't belong in a focused quiz funnel -----
  // The mobile bottom-nav (a third-party app) is injected at runtime and pinned
  // to the bottom of the viewport. We hide any such bar while the quiz is shown
  // (and keep it hidden via a MutationObserver, since the app re-injects it).
  // It is restored automatically on navigation away (full page reload).
  QuizRunner.prototype._hideBottomBars = function () {
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var all = document.body ? document.body.getElementsByTagName("*") : [];
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.__quizHidden) continue;
      if (this.root.contains(el)) continue; // never touch our own UI
      var cs;
      try {
        cs = window.getComputedStyle(el);
      } catch (e) {
        continue;
      }
      if (cs.position !== "fixed" && cs.position !== "sticky") continue;
      if (cs.display === "none" || cs.visibility === "hidden") continue;
      var r = el.getBoundingClientRect();
      // A bar pinned to the bottom edge, spanning most of the width.
      if (
        r.height > 0 &&
        r.height < vh * 0.35 &&
        r.bottom >= vh - 6 &&
        r.top > vh * 0.5 &&
        r.width > vw * 0.6
      ) {
        el.__quizHidden = true;
        el.setAttribute("data-quiz-hidden", "");
        el.style.setProperty("display", "none", "important");
      }
    }
  };

  // Keep the bottom bar hidden even though the nav app re-injects it after load.
  QuizRunner.prototype._watchBottomBars = function () {
    var self = this;
    self._hideBottomBars();
    if (!window.MutationObserver || self._barObserver) return;
    var scheduled = false;
    self._barObserver = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        self._hideBottomBars();
      });
    });
    self._barObserver.observe(document.body, { childList: true, subtree: true });
  };

  // ----- Viewport lock (ports the React useEffect that fills the viewport) -----
  QuizRunner.prototype._lockScroll = function () {
    this._hideBottomBars();
    var el = this.root;
    // The browser may restore a previous scroll position on refresh (e.g. after
    // scrolling the results page). Reset to the top BEFORE measuring, otherwise
    // we lock the body at a scrolled-down offset and the quiz appears stuck.
    if (window.pageYOffset > 0) window.scrollTo(0, 0);
    var top = el.getBoundingClientRect().top;
    var h = Math.max(0, window.innerHeight - Math.max(0, top)) + "px";
    el.style.minHeight = h;
    el.style.maxHeight = h;
    el.style.overflow = "hidden";
    document.documentElement.classList.add("quiz-lock-scroll");
    document.body.classList.add("quiz-lock-scroll");
  };

  QuizRunner.prototype._unlockScroll = function () {
    var el = this.root;
    el.style.minHeight = "";
    el.style.maxHeight = "";
    el.style.overflow = "";
    document.documentElement.classList.remove("quiz-lock-scroll");
    document.body.classList.remove("quiz-lock-scroll");
  };

  // ----- Boot -----
  function init() {
    var roots = document.querySelectorAll("[data-quiz-root]");
    // Don't let the browser restore a stale scroll position on refresh — the
    // quiz locks body scroll, so a restored offset would freeze it mid-page.
    if (roots.length && "scrollRestoration" in window.history) {
      try {
        window.history.scrollRestoration = "manual";
      } catch (e) {
        /* no-op */
      }
    }
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__quizBooted) return;
      root.__quizBooted = true;
      var runner = new QuizRunner(root);
      var relock = function () {
        if (!runner.result && runner.stack.length) runner._lockScroll();
      };
      // Re-apply viewport lock on resize / orientation change.
      window.addEventListener("resize", relock);
      window.addEventListener("orientationchange", relock);
      // The bottom-nav app injects its fixed bar AFTER load — re-measure a few
      // times so the footer ends up above it once it appears.
      [400, 1000, 2000, 3500].forEach(function (ms) {
        setTimeout(relock, ms);
      });
      runner.start();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
